import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewContainerRef,
} from '@angular/core';
import { Timer, TimeUtils } from '@freelancer/time-utils';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import * as Rx from 'rxjs';
import { delay, switchMap, take, tap } from 'rxjs/operators';

export interface CalloutTriggerOpen {
  isPinnedOnClick?: boolean;
}

@Component({
  selector: 'fl-callout-trigger',
  template: ` <ng-content></ng-content> `,
  styleUrls: ['./callout-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalloutTriggerComponent implements OnDestroy, OnInit {
  @Output() trigger = new EventEmitter<MouseEvent>();
  @Output() open = new EventEmitter<CalloutTriggerOpen>();
  @Output() close = new EventEmitter<void>();

  hover: boolean;
  mobileLongPress: boolean;
  isHoverDisabled = false;

  @HostBinding('attr.data-mobile-force-click') mobileForceClick: boolean;

  private longPressTimeout: Timer;
  readonly LONG_PRESS_DELAY = 500;

  private scrollTimeout: Timer;
  private windowScroll?: Rx.Subscription;
  private scrollPos: number;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public containerRef: ViewContainerRef,
    private timeUtils: TimeUtils,
  ) {}

  @HostListener('click', ['$event'])
  handleTriggerClick(event: MouseEvent) {
    /**
     * Force open if hover is set to true
     * to support touch devices as well.
     *
     * Note: Clicking the trigger again or outside
     * the callout will close the callout.
     */
    if (!this.isMobileViewport() && this.hover) {
      this.open.emit({ isPinnedOnClick: true });

      return;
    }

    if (
      this.isMobileViewport() &&
      this.mobileLongPress &&
      !this.mobileForceClick
    ) {
      return;
    }

    this.trigger.emit(event);
  }

  @HostListener('mouseenter')
  handleTriggerMouseover() {
    // Prevent this from running on touch device as it suppress
    // click event from triggering
    if (
      !this.isMobileViewport() &&
      !this.isTouchDevice() &&
      !this.isHoverDisabled
    ) {
      this.open.emit();
    }
  }

  @HostListener('mouseleave')
  handleTriggerMouseout() {
    if (!this.isMobileViewport() && !this.isTouchDevice()) {
      this.close.emit();
    }
  }

  @HostListener('touchstart')
  handleTouchStart() {
    if (!this.mobileLongPress) {
      return;
    }

    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }

    this.longPressTimeout = this.timeUtils.setTimeout(() => {
      this.open.emit();
    }, this.LONG_PRESS_DELAY);
  }

  @HostListener('touchend')
  handlleTouchEnd() {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
  }

  getElementBoundingRect() {
    return (this.containerRef.element
      .nativeElement as HTMLElement).getBoundingClientRect();
  }

  setOptions(options: {
    hover: boolean;
    mobileForceClick: boolean;
    mobileLongPress: boolean;
  }) {
    this.hover = options.hover;
    this.mobileForceClick = options.mobileForceClick;
    this.mobileLongPress = options.mobileLongPress;
  }

  ngOnInit() {
    if (this.hover && isPlatformBrowser(this.platformId)) {
      this.disableHoverWhileScrolling();
    }
  }

  private isMobileViewport() {
    return window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
  }

  private isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  private destroyScrollListener() {
    if (this.windowScroll) {
      this.windowScroll.unsubscribe();
    }
  }

  /**
   * Disable hover from emitting while the user is scrolling.
   * This listens to the first scroll event and temporarily disable hover
   * which will then keep on checking if user is done scrolling for every 500ms
   */
  private disableHoverWhileScrolling(scrollCheckRetry = false) {
    this.destroyScrollListener();

    this.windowScroll = Rx.of(true)
      .pipe(
        switchMap(() =>
          !scrollCheckRetry
            ? // eslint-disable-next-line local-rules/no-scroll-listener
              Rx.fromEvent(window, 'scroll').pipe(take(1))
            : Rx.of(true),
        ),
        tap(() => {
          this.scrollPos = window.scrollY;
          this.isHoverDisabled = true;
        }),
        delay(500),
      )
      .subscribe(() => {
        /**
         * Check if scroll position changed after 500ms
         * If true, keep hover disabled and check again after 500ms
         * Else, enable hover and wait for a new scroll event to fire
         */
        const scrollPosChanged = this.scrollPos !== window.scrollY;

        this.isHoverDisabled = scrollPosChanged;
        this.disableHoverWhileScrolling(scrollPosChanged);
      });
  }

  ngOnDestroy() {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.destroyScrollListener();
  }
}
